/*!glb-autoplay-selthumb.js

  Cosa fa questo script:
  - Identifica in modo robusto la thumbnail corrispondente al primo model-viewer / 3D media visibile su una pagina prodotto.
  - Esegue una "selezione" sintetica della thumb (safeClick) che prova click nativo, dispatch di eventi mouse/keyboard e segnali custom per integrarsi con diversi sistemi di autoplay/3D.
  - Offre un'API globale minimale per triggerare manualmente la selezione, cercare thumb e pulire le risorse.
  - Usa debounce e un observer throttled per minimizzare l'impatto sulle prestazioni e per adattarsi a caricamenti dinamici, carousel e shadow DOM.

  Funzioni esposte (window.__GLB_AUTOPLAY_SELTHUMB__):
  - trigger(identifierOrTarget): avvia la ricerca e la selezione della thumb; ritorna {ok, info, node}
  - triggerDebounced: funzione debounce esportata per invocazioni ripetute senza sovraccarico
  - findThumb(identifierOrTarget): ritorna l'elemento thumb individuato (o null)
  - candidateMatches(node, identifier): valuta se un nodo corrisponde alle euristiche 3D
  - cleanup(): cancella timeout, disconnect dell'observer e rimuove l'API globale
*/

(function () {
  'use strict';

  if (window.__GLB_AUTOPLAY_SELTHUMB__ && window.__GLB_AUTOPLAY_SELTHUMB__._installed) return;

  const CONFIG = {
    DEBOUNCE_MS: 250,
    AUTORUN: true,
    ENABLE_ONTHUMB: true,
    ENABLE_SHADOW_TRAVERSE: true,
    OBSERVER_ENABLED: true,
    OBSERVER_THROTTLE_MS: 200,
    OBSERVER_MAX_WORK: 50
  };

  const IMG_SEL = 'img.imgFt';
  const KEYWORDS = ['3dmodel', '3d', 'glb', 'gltf', 'usdz', 'model'];

  const containsKeyword = (v) => {
    if (!v) return false;
    const s = String(v).toLowerCase();
    return KEYWORDS.some(k => s.indexOf(k) !== -1);
  };

  // Determina se un elemento IMG è probabilmente una thumbnail 3D
  function isThumbImg(el) {
    if (!el || el.tagName !== 'IMG') return false;
    if (!el.classList || !el.classList.contains('imgFt')) return false;
    const src = el.currentSrc || el.src || '';
    return containsKeyword(src.toLowerCase());
  }

  // Query che attraversa anche shadowRoot per trovare elementi corrispondenti al selettore
  function queryAllPiercing(root, selector) {
    const out = [];
    const stack = [root || document];
    while (stack.length) {
      const node = stack.pop();
      if (!node) continue;
      try {
        if (node.querySelectorAll) {
          const q = node.querySelectorAll(selector);
          for (let i = 0; i < q.length; i++) out.push(q[i]);
        }
      } catch (e) {}
      const children = node.children || [];
      for (let i = 0; i < children.length; i++) {
        const c = children[i];
        if (c && c.shadowRoot) stack.push(c.shadowRoot);
        stack.push(c);
      }
    }
    return out;
  }

  // Se il target è già dentro una struttura "thumb-style", trova l'IMG corrispondente risalendo la DOM
  function findThumbOnthumbStyleFromTarget(target) {
    if (!CONFIG.ENABLE_ONTHUMB || !target) return null;
    let n = target;
    while (n && n !== document.body) {
      if (isThumbImg(n)) return n;
      if (n.querySelector) {
        try {
          const inside = n.querySelector(IMG_SEL);
          if (inside && isThumbImg(inside)) return inside;
        } catch (e) {}
      }
      n = n.parentElement;
    }
    const g = document.querySelector(IMG_SEL);
    return g && isThumbImg(g) ? g : null;
  }

  // Euristiche multi-attr per riconoscere nodi che rappresentano media 3D
  function candidateMatchesNode(node, identifier) {
    if (!node) return false;

    try {
      if (CONFIG.ENABLE_ONTHUMB && isThumbImg(node)) return true;
      if (CONFIG.ENABLE_ONTHUMB && node.querySelector) {
        const inside = node.querySelector(IMG_SEL);
        if (inside && isThumbImg(inside)) return true;
      }
    } catch (e) {}

    if (node.getAttribute) {
      try {
        const a = node.getAttribute.bind(node);
        const attrStr = String(a('href') || a('data-glb-src') || a('data-media-src') || a('data-image') || a('src') || '').toLowerCase();
        if (containsKeyword(attrStr)) return true;
        const dtype = String(a('data-media-type') || a('data-type') || '').toLowerCase();
        if (dtype && /3d|model/.test(dtype)) return true;
      } catch (e) {}
    }

    if (node.tagName && node.tagName.toLowerCase().indexOf('product-model') !== -1) return true;

    try {
      if ('alt' in node && node.alt && containsKeyword(node.alt)) return true;
      if ('title' in node && node.title && containsKeyword(node.title)) return true;
      if (node.className && containsKeyword(String(node.className))) return true;
      const aria = node.getAttribute ? node.getAttribute('aria-label') || '' : '';
      if (aria && containsKeyword(aria)) return true;
    } catch (e) {}

    if (identifier && node.getAttributeNames) {
      try {
        const idstr = String(identifier);
        const attrs = node.getAttributeNames();
        for (let i = 0; i < attrs.length; i++) {
          const val = node.getAttribute(attrs[i]);
          if (val && String(val).indexOf(idstr) !== -1) return true;
        }
      } catch (e) {}
    }

    return false;
  }

  // Generatore che restituisce nodi candidati, con o senza traversal dello shadow DOM
  function* iterateCandidateNodes(root = document) {
    const selList = ['a[href]', 'img', 'product-model,model-viewer,[data-media-id],[data-media-type]', '[data-image],[data-glb-src],[data-media-src]'];
    if (CONFIG.ENABLE_SHADOW_TRAVERSE) {
      for (let s = 0; s < selList.length; s++) {
        const q = queryAllPiercing(root, selList[s]);
        for (let i = 0; i < q.length; i++) yield q[i];
      }
      return;
    }
    for (let s = 0; s < selList.length; s++) {
      const q = root.querySelectorAll(selList[s]);
      for (let i = 0; i < q.length; i++) yield q[i];
    }
  }

  // Cerca la thumb usando più strategie: contesti contenitori noti, target diretto, fallback globale
  function findThumb(identifierOrTarget) {
    let identifier = null;
    if (identifierOrTarget && (identifierOrTarget instanceof Event)) identifierOrTarget = identifierOrTarget.target || identifierOrTarget;

    if (identifierOrTarget && identifierOrTarget instanceof Element) {
      const t = findThumbOnthumbStyleFromTarget(identifierOrTarget);
      if (t) return t;
      identifier = identifierOrTarget;
    } else if (identifierOrTarget) identifier = identifierOrTarget;

    const containers = [
      '[data-product-media]', '[data-media-gallery]', '.product__media', '.product-single__media',
      '.product__media-list', '.media-thumbs', '.product-thumbnails'
    ];

    function candidatePreferImg(node, identifier) {
      if (!node) return null;
      try { if (node.tagName && node.tagName.toLowerCase() === 'img' && candidateMatchesNode(node, identifier)) return node; } catch (e) {}
      try {
        if (identifier && identifier instanceof Element) {
          if (node.isSameNode && node.isSameNode(identifier)) return node;
          if (node.contains && node.contains(identifier)) return node;
          if (identifier.contains && identifier.contains(node)) return node;
        }
      } catch (e) {}
      if (identifier && !(identifier instanceof Element) && node.getAttribute) {
        try {
          const idstr = String(identifier);
          const attrs = node.getAttributeNames ? node.getAttributeNames() : [];
          for (let i = 0; i < attrs.length; i++) {
            const v = node.getAttribute(attrs[i]);
            if (v && String(v).indexOf(idstr) !== -1) return node;
          }
        } catch (e) {}
      }
      try {
        if (node.querySelector) {
          const inside = node.querySelector(IMG_SEL);
          if (inside && candidateMatchesNode(inside, identifier)) return inside;
        }
      } catch (e) {}
      return candidateMatchesNode(node, identifier) ? node : null;
    }

    for (let i = 0; i < containers.length; i++) {
      const root = document.querySelector(containers[i]);
      if (!root) continue;
      for (const n of iterateCandidateNodes(root)) {
        const prefer = candidatePreferImg(n, identifier);
        if (prefer) return prefer;
      }
    }

    for (const n of iterateCandidateNodes(document)) {
      const prefer = candidatePreferImg(n, identifier);
      if (prefer) return prefer;
    }

    const fallback = document.querySelector(IMG_SEL);
    if (fallback && isThumbImg(fallback)) return fallback;
    return null;
  }

  // Crea CustomEvent compatibile legacy
  function createCustomEvent(name, detail) {
    try { return new CustomEvent(name, { bubbles: true, detail }); }
    catch (e) {
      const ev = document.createEvent('CustomEvent');
      ev.initCustomEvent(name, true, false, detail);
      return ev;
    }
  }

  // Crea MouseEvent click compatibile legacy
  function createMouseClick() {
    try { return new MouseEvent('click', { bubbles: true, cancelable: true }); }
    catch (e) {
      const ev = document.createEvent('MouseEvents');
      ev.initMouseEvent('click', true, true, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null);
      return ev;
    }
  }

  // Simula attivazione da tastiera per garantire accessibilità e integrazione con listener keyboard
  function dispatchKeyboardActivation(el, originalTarget) {
    if (!el && !originalTarget) return;
    const targets = [];
    if (el) targets.push(el);
    if (originalTarget && originalTarget !== el) targets.push(originalTarget);

    const primary = targets[0];
    let hadTabIndex = false;
    let prevTabIndex = null;
    if (primary && primary.hasAttribute) {
      hadTabIndex = primary.hasAttribute('tabindex');
      prevTabIndex = hadTabIndex ? primary.getAttribute('tabindex') : null;
      if (!hadTabIndex) {
        try { primary.setAttribute('tabindex', '0'); } catch (e) {}
      }
      try { primary.focus && primary.focus(); } catch (e) {}
    }

    function makeKeyEvent(type, key, code, keyCode) {
      try {
        const ev = new KeyboardEvent(type, { bubbles: true, cancelable: true, composed: true, key, code });
        try {
          if (ev.keyCode !== keyCode) {
            Object.defineProperty(ev, 'keyCode', { value: keyCode, configurable: true });
            Object.defineProperty(ev, 'which', { value: keyCode, configurable: true });
          }
        } catch (e) {}
        return ev;
      } catch (e) {
        try {
          const ev = document.createEvent('Event');
          ev.initEvent(type, true, true);
          Object.defineProperty(ev, 'key', { value: key, configurable: true });
          Object.defineProperty(ev, 'code', { value: code, configurable: true });
          Object.defineProperty(ev, 'keyCode', { value: keyCode, configurable: true });
          Object.defineProperty(ev, 'which', { value: keyCode, configurable: true });
          return ev;
        } catch (e2) {
          return null;
        }
      }
    }

    const seq = [
      ['keydown', 'Enter', 'Enter', 13],
      ['keypress', 'Enter', 'Enter', 13],
      ['keyup', 'Enter', 'Enter', 13],
      ['keydown', ' ', 'Space', 32],
      ['keypress', ' ', 'Space', 32],
      ['keyup', ' ', 'Space', 32]
    ];

    for (let i = 0; i < seq.length; i++) {
      const ev = makeKeyEvent(seq[i][0], seq[i][1], seq[i][2], seq[i][3]);
      if (!ev) continue;
      for (let j = 0; j < targets.length; j++) {
        try { targets[j] && targets[j].dispatchEvent(ev); } catch (e) {}
      }
      try { document.dispatchEvent(ev); } catch (e) {}
      try { window.dispatchEvent(ev); } catch (e) {}
    }

    if (primary && !hadTabIndex) {
      try { primary.removeAttribute('tabindex'); } catch (e) {}
    } else if (primary && prevTabIndex !== null) {
      try { primary.setAttribute('tabindex', prevTabIndex); } catch (e) {}
    }
  }

  // Tentativo robusto di click sulla thumb: click nativo, eventi custom, stato visivo/ARIA e fallback mouse/keyboard
  function safeClick(el) {
    if (!el) return false;
    if (el.querySelector) {
      try {
        const inside = el.querySelector(IMG_SEL);
        if (inside && isThumbImg(inside)) el = inside;
      } catch (e) {}
    }

    const tag = (el.tagName || '').toLowerCase();

    if (tag === 'a' || tag === 'button') {
      try { el.click(); } catch (e) {
        try { el.dispatchEvent(createMouseClick()); } catch (e2) {}
      }
    }

    if (el.classList) try { el.classList.add('is-selected'); } catch (e) {}
    if (el.setAttribute) try { el.setAttribute('aria-selected', 'true'); } catch (e) {}

    const getAttr = el.getAttribute && el.getAttribute.bind(el);
    const mid = getAttr ? (getAttr('data-media-id') || getAttr('data-id') || null) : null;
    const payload = { mediaId: mid || null, id: mid || null, source: 'glb-autoplay-selthumb' };
    const customEvents = ['media:select', 'media:change', 'product:media:select', 'glb:select', 'glb:thumb:click'];

    for (let i = 0; i < customEvents.length; i++) {
      const ev = createCustomEvent(customEvents[i], payload);
      try { el.dispatchEvent(ev); } catch (e) {}
      try { document.dispatchEvent(ev); } catch (e) {}
      try { document.body.dispatchEvent(ev); } catch (e) {}
    }

    try { el.dispatchEvent(createMouseClick()); } catch (e) {}
    dispatchKeyboardActivation(el);
    return true;
  }

  // Debounce cancellabile
  function debounce(fn, ms) {
    let t = null;
    const debounced = function (...args) {
      if (t) clearTimeout(t);
      t = setTimeout(() => { t = null; fn.apply(this, args); }, ms);
    };
    debounced.cancel = function () { if (t) { clearTimeout(t); t = null; } };
    return debounced;
  }

  // MutationObserver con batching e throttling per limitare lavoro in frame intensi
  function createThrottledObserver(onChange, { timeout = 200, maxWorkPerTick = 50 } = {}) {
    let timer = null, queue = [];
    const process = function () {
      const work = queue.splice(0, maxWorkPerTick);
      try { onChange(work); } catch (e) {}
      if (queue.length) timer = setTimeout(process, timeout);
      else timer = null;
    };
    const obs = new MutationObserver((mutations) => {
      for (let i = 0; i < mutations.length; i++) queue.push(mutations[i]);
      if (!timer) timer = setTimeout(process, timeout);
    });
    return {
      observe(root, opts) { try { obs.observe(root || document, opts || { childList: true, subtree: true }); } catch (e) {} },
      disconnect() { try { obs.disconnect(); } catch (e) {} if (timer) { clearTimeout(timer); timer = null; queue = []; } }
    };
  }

  let _autorunTimeout = null;
  let _debouncedTrigger = null;
  let _observerHandle = null;

  // Ricerca thumb + selezione + delega ad eventuali API autoplay esterne
  async function runSelectAndDelegate(identifierOrTarget) {
    const thumb = findThumb(identifierOrTarget);
    if (!thumb) return { ok: false, info: 'not-found' };
    try { if (thumb.scrollIntoView) thumb.scrollIntoView({ block: 'nearest', behavior: 'auto' }); } catch (e) {}
    safeClick(thumb);

    if (typeof window.__glb_start_model_viewer === 'function') {
      try { await window.__glb_start_model_viewer({ waitMs: 700 }); return { ok: true, info: 'selected-and-started-via-__glb_start_model_viewer', node: thumb }; } catch (e) {}
    }

    if (typeof window.__glb_autostart_scheduleRun === 'function') {
      try { window.__glb_autostart_scheduleRun(120); return { ok: true, info: 'selected-and-scheduled-via-__glb_autostart_scheduleRun', node: thumb }; } catch (e) {}
    }

    return { ok: true, info: 'selected-no-external-autoplay-api', node: thumb };
  }

  const API = {
    _installed: true,
    trigger: async function (identifierOrTarget) { return await runSelectAndDelegate(identifierOrTarget || null); },
    triggerDebounced: null,
    findThumb: function (identifierOrTarget) { return findThumb(identifierOrTarget); },
    candidateMatches: function (node, identifier) { return candidateMatchesNode(node, identifier); },
    cleanup: function () {
      if (_autorunTimeout) { clearTimeout(_autorunTimeout); _autorunTimeout = null; }
      if (_debouncedTrigger && _debouncedTrigger.cancel) _debouncedTrigger.cancel();
      if (_observerHandle && _observerHandle.disconnect) try { _observerHandle.disconnect(); } catch (e) {}
      try { delete window.__GLB_AUTOPLAY_SELTHUMB__; } catch (e) {}
    }
  };

  // Esponi API in anticipo
  window.__GLB_AUTOPLAY_SELTHUMB__ = window.__GLB_AUTOPLAY_SELTHUMB__ || API;

  (function installDebounced() {
    const deb = debounce(function (identifierOrTarget) { API.trigger(identifierOrTarget); }, CONFIG.DEBOUNCE_MS);
    API.triggerDebounced = deb;
    _debouncedTrigger = deb;
  })();

  if (CONFIG.AUTORUN) {
    _autorunTimeout = setTimeout(() => {
      if (window.__GLB_AUTOPLAY_SELTHUMB__ && window.__GLB_AUTOPLAY_SELTHUMB__.triggerDebounced) {
        window.__GLB_AUTOPLAY_SELTHUMB__.triggerDebounced();
      }
      _autorunTimeout = null;
    }, 200);
  }

  if (CONFIG.OBSERVER_ENABLED) {
    try {
      const containers = [
        '[data-product-media]', '[data-media-gallery]', '.product__media', '.product-single__media',
        '.product__media-list', '.media-thumbs', '.product-thumbnails'
      ];
      const onChange = (mutations) => {
        for (let i = 0; i < mutations.length; i++) {
          const m = mutations[i];
          const t = m.target || (m.addedNodes && m.addedNodes[0]) || null;
          if (t) { try { API.triggerDebounced(t); return; } catch (e) {} }
        }
        API.triggerDebounced();
      };
      _observerHandle = createThrottledObserver(onChange, { timeout: CONFIG.OBSERVER_THROTTLE_MS, maxWorkPerTick: CONFIG.OBSERVER_MAX_WORK });
      for (let i = 0; i < containers.length; i++) {
        const root = document.querySelector(containers[i]);
        if (root) _observerHandle.observe(root, { childList: true, subtree: true });
      }
      if (!_observerHandle) _observerHandle.observe(document, { childList: true, subtree: true });
    } catch (e) {}
  }

})();
